import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useCalculateBasketPricing,
  useCalculateCheckoutPricing,
  useCreatePricingQuote,
  useCreatePricingSnapshot,
  usePricingAuditLog,
  usePricingQuote,
  usePricingSnapshotByOrderId,
  useProductPrice,
  useProviderConfigs,
  useProviderCredentialStatus,
  useSetProviderCredential,
  useValidatePromoPricing,
} from '../../hooks';
import { formatDateTime, formatPrice } from '../../lib/format';
import type { ToastSeverity } from '../../lib/useToast';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const BASKET_SAMPLE = '{\n  "items": [\n    { "productId": "", "quantity": 1, "selected": true }\n  ]\n}';
const CHECKOUT_SAMPLE = '{\n  "fulfillmentType": "PICKUP",\n  "items": [\n    { "productId": "", "quantity": 1, "selected": true }\n  ]\n}';
const PROMO_SAMPLE = '{\n  "promoCode": "",\n  "productIds": []\n}';

function parseJson<T>(raw: string): T {
  return JSON.parse(raw) as T;
}

function JsonResult({ value }: { value: unknown }) {
  if (value === undefined) return null;
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'grey.50',
        border: (t) => `1px solid ${t.palette.divider}`,
        overflow: 'auto',
        maxHeight: 360,
        fontSize: 12,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
          </Stack>
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PriceOperationsPanel({ canManage, onResult }: PanelProps) {
  const { t } = useTranslation('catalog');
  const [productId, setProductId] = useState('');
  const productPrice = useProductPrice(productId.trim() || undefined, { enabled: false });

  const [basketJson, setBasketJson] = useState(BASKET_SAMPLE);
  const [checkoutJson, setCheckoutJson] = useState(CHECKOUT_SAMPLE);
  const [promoJson, setPromoJson] = useState(PROMO_SAMPLE);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const basket = useCalculateBasketPricing();
  const checkout = useCalculateCheckoutPricing();
  const promo = useValidatePromoPricing();
  const createQuote = useCreatePricingQuote();

  const [quoteId, setQuoteId] = useState('');
  const quoteQuery = usePricingQuote(quoteId.trim() || undefined, { enabled: false });
  const [snapshotQuoteId, setSnapshotQuoteId] = useState('');
  const [snapshotOrderId, setSnapshotOrderId] = useState('');
  const createSnapshot = useCreatePricingSnapshot();
  const [snapshotLookupOrderId, setSnapshotLookupOrderId] = useState('');
  const snapshotQuery = usePricingSnapshotByOrderId(snapshotLookupOrderId.trim() || undefined, { enabled: false });

  const providers = useProviderConfigs();
  const [providerId, setProviderId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const credentialStatus = useProviderCredentialStatus(providerId || undefined, { enabled: Boolean(providerId) });
  const setCredential = useSetProviderCredential();

  const [customerId, setCustomerId] = useState('');
  const auditParams = useMemo(
    () => ({ customerId: customerId.trim() || undefined, page: 0, size: 20 }),
    [customerId],
  );
  const runtimeAudit = usePricingAuditLog(auditParams);

  const runJsonMutation = async <T,>(raw: string, action: (body: T) => Promise<unknown>) => {
    setJsonError(null);
    try {
      await action(parseJson<T>(raw));
    } catch (error) {
      if (error instanceof SyntaxError) {
        setJsonError(t('priceOperationsPanel.invalidJson'));
      } else {
        onResult('error', error instanceof Error ? error.message : t('common:error.unknown'));
      }
    }
  };

  if (providers.isLoading) {
    return <LoadingState label={t('priceOperationsPanel.loading')} />;
  }
  if (providers.isError) {
    return <ErrorState message={providers.error?.message} onRetry={() => void providers.refetch()} />;
  }

  return (
    <Stack spacing={2.5}>
      {jsonError ? <Alert severity="error">{jsonError}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
        <SectionCard title={t('priceOperationsPanel.product.title')} icon={<SearchRoundedIcon color="primary" />}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label={t('priceOperationsPanel.product.productId')}
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={productPrice.isFetching ? <CircularProgress size={16} color="inherit" /> : <SearchRoundedIcon />}
              disabled={!productId.trim() || productPrice.isFetching}
              onClick={() => void productPrice.refetch()}
            >
              {t('priceOperationsPanel.actions.lookup')}
            </Button>
          </Stack>
          {productPrice.data ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={t('priceOperationsPanel.product.price', { value: formatPrice(productPrice.data.price) })} />
              <Chip label={t('priceOperationsPanel.product.base', { value: formatPrice(productPrice.data.basePrice) })} />
              <Chip label={t('priceOperationsPanel.product.discount', { value: formatPrice(productPrice.data.totalDiscount) })} />
            </Stack>
          ) : null}
        </SectionCard>

        <SectionCard title={t('priceOperationsPanel.promo.title')} icon={<LocalOfferRoundedIcon color="primary" />}>
          <TextField
            label={t('priceOperationsPanel.requestJson')}
            value={promoJson}
            onChange={(e) => setPromoJson(e.target.value)}
            multiline
            minRows={5}
          />
          <Button
            variant="outlined"
            startIcon={promo.isPending ? <CircularProgress size={16} /> : <LocalOfferRoundedIcon />}
            disabled={promo.isPending}
            onClick={() => void runJsonMutation(promoJson, promo.mutateAsync)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('priceOperationsPanel.actions.validate')}
          </Button>
          <JsonResult value={promo.data} />
        </SectionCard>

        <SectionCard title={t('priceOperationsPanel.basket.title')} icon={<CalculateRoundedIcon color="primary" />}>
          <TextField
            label={t('priceOperationsPanel.requestJson')}
            value={basketJson}
            onChange={(e) => setBasketJson(e.target.value)}
            multiline
            minRows={8}
          />
          <Button
            variant="contained"
            startIcon={basket.isPending ? <CircularProgress size={16} color="inherit" /> : <CalculateRoundedIcon />}
            disabled={basket.isPending}
            onClick={() => void runJsonMutation(basketJson, basket.mutateAsync)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('priceOperationsPanel.actions.calculate')}
          </Button>
          <JsonResult value={basket.data} />
        </SectionCard>

        <SectionCard title={t('priceOperationsPanel.checkout.title')} icon={<ReceiptLongRoundedIcon color="primary" />}>
          <TextField
            label={t('priceOperationsPanel.requestJson')}
            value={checkoutJson}
            onChange={(e) => setCheckoutJson(e.target.value)}
            multiline
            minRows={8}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="contained"
              startIcon={checkout.isPending ? <CircularProgress size={16} color="inherit" /> : <CalculateRoundedIcon />}
              disabled={checkout.isPending}
              onClick={() => void runJsonMutation(checkoutJson, checkout.mutateAsync)}
            >
              {t('priceOperationsPanel.actions.calculate')}
            </Button>
            <Button
              variant="outlined"
              startIcon={createQuote.isPending ? <CircularProgress size={16} /> : <ReceiptLongRoundedIcon />}
              disabled={createQuote.isPending}
              onClick={() => void runJsonMutation(checkoutJson, createQuote.mutateAsync)}
            >
              {t('priceOperationsPanel.actions.createQuote')}
            </Button>
          </Stack>
          <JsonResult value={checkout.data ?? createQuote.data} />
        </SectionCard>
      </Box>

      <SectionCard title={t('priceOperationsPanel.quotes.title')} icon={<ReceiptLongRoundedIcon color="primary" />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Stack spacing={1.5}>
            <TextField label={t('priceOperationsPanel.quotes.quoteId')} value={quoteId} onChange={(e) => setQuoteId(e.target.value)} />
            <Button variant="outlined" disabled={!quoteId.trim()} onClick={() => void quoteQuery.refetch()}>
              {t('priceOperationsPanel.actions.lookup')}
            </Button>
            <JsonResult value={quoteQuery.data} />
          </Stack>
          <Stack spacing={1.5}>
            <TextField label={t('priceOperationsPanel.quotes.snapshotQuoteId')} value={snapshotQuoteId} onChange={(e) => setSnapshotQuoteId(e.target.value)} />
            <TextField label={t('priceOperationsPanel.quotes.orderId')} value={snapshotOrderId} onChange={(e) => setSnapshotOrderId(e.target.value)} />
            <Button
              variant="contained"
              disabled={!canManage || !snapshotQuoteId.trim() || !snapshotOrderId.trim() || createSnapshot.isPending}
              onClick={() =>
                createSnapshot.mutate(
                  { quoteId: snapshotQuoteId.trim(), orderId: snapshotOrderId.trim() },
                  { onSuccess: () => onResult('success', t('priceOperationsPanel.quotes.snapshotCreated')) },
                )
              }
            >
              {t('priceOperationsPanel.actions.createSnapshot')}
            </Button>
            <Divider />
            <TextField label={t('priceOperationsPanel.quotes.lookupOrderId')} value={snapshotLookupOrderId} onChange={(e) => setSnapshotLookupOrderId(e.target.value)} />
            <Button variant="outlined" disabled={!snapshotLookupOrderId.trim()} onClick={() => void snapshotQuery.refetch()}>
              {t('priceOperationsPanel.actions.lookup')}
            </Button>
            <JsonResult value={createSnapshot.data ?? snapshotQuery.data} />
          </Stack>
        </Box>
      </SectionCard>

      <Box sx={{ maxWidth: { lg: '50%' } }}>
        <SectionCard title={t('priceOperationsPanel.credentials.title')} icon={<KeyRoundedIcon color="primary" />}>
          <TextField
            select
            label={t('priceOperationsPanel.credentials.provider')}
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
          >
            {(providers.data ?? []).map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.providerName} / {provider.baseUrl}
              </MenuItem>
            ))}
          </TextField>
          {credentialStatus.data ? (
            <Chip
              color={credentialStatus.data.configured ? 'success' : 'default'}
              label={
                credentialStatus.data.configured
                  ? t('priceOperationsPanel.credentials.configured')
                  : t('priceOperationsPanel.credentials.notConfigured')
              }
              sx={{ alignSelf: 'flex-start' }}
            />
          ) : null}
          <TextField
            label={t('priceOperationsPanel.credentials.apiKey')}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            type="password"
            disabled={!canManage}
          />
          <Button
            variant="contained"
            startIcon={setCredential.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
            disabled={!canManage || !providerId || !apiKey.trim() || setCredential.isPending}
            onClick={() =>
              setCredential.mutate(
                { configId: providerId, body: { apiKey: apiKey.trim() } },
                {
                  onSuccess: () => {
                    setApiKey('');
                    onResult('success', t('priceOperationsPanel.credentials.saved'));
                  },
                  onError: (error) => onResult('error', error.message),
                },
              )
            }
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('common:action.save')}
          </Button>
        </SectionCard>
      </Box>

      <SectionCard title={t('priceOperationsPanel.audit.title')} icon={<ManageSearchRoundedIcon color="primary" />}>
        <TextField
          label={t('priceOperationsPanel.audit.customerId')}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          sx={{ maxWidth: 520 }}
        />
        {runtimeAudit.isLoading ? <LoadingState label={t('priceOperationsPanel.loading')} fullHeight={false} /> : null}
        {runtimeAudit.isError ? (
          <ErrorState message={runtimeAudit.error?.message} onRetry={() => void runtimeAudit.refetch()} />
        ) : (
          <JsonResult value={runtimeAudit.data} />
        )}
        {runtimeAudit.data?.content?.[0]?.createdAt ? (
          <Typography variant="caption" color="text.secondary">
            {t('priceOperationsPanel.audit.lastEntry', { value: formatDateTime(runtimeAudit.data.content[0].createdAt) })}
          </Typography>
        ) : null}
      </SectionCard>
    </Stack>
  );
}
